import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import api from "../api";
import { COMPANIES, HEADER_COMPANY_KEY } from "../companies";

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionExp, setSessionExp] = useState(null); // epoch (secondi) di scadenza del token di sessione
  const [maintenance, setMaintenance] = useState({ active: false, message: "", eta: "" });
  const [brandCompany, setBrandCompanyState] = useState(() => {
    const c = localStorage.getItem(HEADER_COMPANY_KEY);
    return COMPANIES[c] ? c : null;
  });

  const setBrandCompany = (code) => {
    if (!COMPANIES[code]) return;
    localStorage.setItem(HEADER_COMPANY_KEY, code);
    setBrandCompanyState(code);
  };

  useEffect(() => {
    // Verifica la sessione tramite cookie httpOnly (inviato automaticamente).
    api
      .get("/auth/me")
      .then((res) => { setUser(res.data); setSessionExp(res.data?.token_exp ?? null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // Stato modalità manutenzione (pubblico): polling + refresh su evento 503.
    let alive = true;
    const fetchM = () => api.get("/maintenance")
      .then((r) => { if (alive) setMaintenance(r.data || { active: false }); })
      .catch(() => {});
    fetchM();
    const iv = setInterval(fetchM, 20000);
    window.addEventListener("tb-maintenance-refresh", fetchM);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("tb-maintenance-refresh", fetchM); };
  }, []);

  const login = async (username, password, company, remember = true) => {
    try {
      const res = await api.post("/auth/login", { username, password, company, remember });
      setUser(res.data.user);
      setSessionExp(res.data?.token_exp ?? null);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Errore di accesso. Riprova." };
    }
  };

  const startDemo = async (role = "admin") => {
    try {
      const res = await api.post(`/demo/start?role=${role}`);
      setUser(res.data.user);
      setSessionExp(res.data?.token_exp ?? null);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.response?.data?.detail || "Impossibile avviare la demo. Riprova." };
    }
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    setUser(null);
    setSessionExp(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      setSessionExp(res.data?.token_exp ?? null);
    } catch (e) {
      console.error("refreshUser failed", e);
    }
  };

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "responsabile";
  // Può approvare SOLO chi è responsabile di almeno un reparto (flag calcolato dal backend).
  // Un admin NON approva più di default: deve essere anche responsabile del reparto.
  const isApprover = !!user?.is_approver;
  const isExternal = !!user?.is_external;

  // Header branding: admin-selected company override, else the user's own company.
  const effectiveBrand = brandCompany || user?.company || "teleborsa";

  const value = useMemo(
    () => ({ user, loading, sessionExp, maintenance, login, startDemo, logout, refreshUser, isAdmin, isManager, isApprover, isExternal, effectiveBrand, brandCompany, setBrandCompany }),
    [user, loading, sessionExp, maintenance, isAdmin, isManager, isApprover, isExternal, effectiveBrand, brandCompany]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
